<template>
  <div :id="'editor' + id" class="address-search">
    <div class="address-search-trigger">
      <div class="input-group input-left">
        <input type="text" class="form-control" readonly :placeholder="$t('addressSearch.placeholder')" @click="$parent.expandPanel" />
      </div>
      <span class="glyphicon glyphicon-search" aria-hidden="true"></span>
    </div>
    <p v-if="canUndo" class="address-search-undo">
      <a href="#" @click.prevent="undo">{{ $t('addressSearch.undo') }}</a>
    </p>

    <div class="editor-panel hidden animated">
      <div class="close" @click="$parent.hidePanel(true)">
        <span class="glyphicon glyphicon-chevron-right"></span>
      </div>
      <div class="row">
        <div class="col-md-7">
          <label class="field-name">{{ title }}</label>
          <p class="field-sub-name">{{ $t('addressSearch.description') }}</p>

          <template v-if="!preview">
            <input
              :id="'address-search-' + id"
              v-model="query"
              type="text"
              class="form-control"
              autocomplete="off"
              role="combobox"
              :aria-expanded="showSuggestions ? 'true' : 'false'"
              :placeholder="$t('addressSearch.queryPlaceholder')"
              @input="searchDebounced"
              @keydown="onSearchKeydown"
            />
            <p class="field-sub-sub-name address-search-source">
              {{ $t('addressSearch.searchesIn') }}
              <select v-model="providerId" @change="onProviderChange">
                <option v-for="provider in providerOptions" :key="provider.id" :value="provider.id">{{ provider.label }}</option>
              </select>
            </p>

            <ul v-if="showSuggestions" class="list-group address-search-suggestions" role="listbox">
              <li v-if="resolving || searching" class="list-group-item address-search-state">{{ $t('addressSearch.searching') }}</li>
              <li v-else-if="searchError" class="list-group-item address-search-state">{{ $t('addressSearch.searchError') }}</li>
              <li v-else-if="!suggestions.length" class="list-group-item address-search-state">{{ $t('addressSearch.noResults') }}</li>
              <template v-else>
                <li
                  v-for="(suggestion, index) in suggestions"
                  :key="suggestion.id"
                  role="option"
                  :aria-selected="index === activeIndex ? 'true' : 'false'"
                  class="list-group-item address-search-suggestion"
                  :class="{ active: index === activeIndex }"
                  @mousedown.prevent="choose(suggestion)"
                >
                  <span class="address-search-suggestion-label">{{ suggestionLabel(suggestion) }}</span>
                  <span class="address-search-suggestion-type">{{ $t('addressSearch.types.' + suggestion.type) }}</span>
                  <span v-if="suggestion.missingHouseNumber" class="address-search-suggestion-warning">
                    {{ $t('addressSearch.notes.houseNumberNotFound', { houseNumber: suggestion.missingHouseNumber }) }}
                  </span>
                </li>
              </template>
            </ul>
          </template>

          <template v-else>
            <p class="address-search-preview-title">
              <strong>{{ preview.title }}</strong>
              <span>{{ $t('addressSearch.from', { source: preview.source }) }}</span>
            </p>
            <p v-if="conflictCount" class="field-sub-sub-name">{{ $tc('addressSearch.conflicts', conflictCount, { count: conflictCount }) }}</p>
            <p v-if="preview.callout" class="address-search-callout">{{ preview.callout }}</p>

            <table class="address-search-preview">
              <tbody>
                <tr v-for="row in preview.rows" :key="row.id" :class="{ skipped: !row.selected }">
                  <td class="address-search-preview-check">
                    <input v-model="row.selected" type="checkbox" :aria-label="row.label" />
                  </td>
                  <td class="address-search-preview-label">{{ row.label }}</td>
                  <td class="address-search-preview-value">
                    <span v-if="row.previous" class="previous">{{ row.previous }}</span>
                    <input
                      v-model="row.value"
                      type="text"
                      class="form-control input-sm"
                      :placeholder="$t('addressSearch.notes.empty')"
                      :aria-label="row.label"
                      @input="row.selected = true"
                      @keydown.enter.prevent="apply"
                    />
                    <span v-for="note in row.notes" :key="note.text" class="note" :class="note.kind">{{ note.text }}</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="address-search-actions">
              <button class="btn address-search-apply" :disabled="!applyCount" @click="apply">{{ $tc('addressSearch.apply', applyCount, { count: applyCount }) }}</button>
              <button class="btn" @click="backToSearch">{{ $t('addressSearch.searchOther') }}</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
'use strict'

import $ from 'jquery'
import _ from 'lodash'
import {
  DEFAULT_PROVIDER_ID,
  PROVIDERS,
  PROJECT_ADDRESS_FIELDS,
  SUGGESTION_TYPES,
  resolveAddress,
  suggestAddresses
} from '@/services/addressSearch/addressSearchService'
import addressSearchUtils, { ADDRESS_SEARCH_MARK_KINDS } from '@/utils/addressSearch/addressSearchUtils'

const MIN_QUERY_LENGTH = 3
// User setting with the source the user picked last (ManagementWS settings, per user).
const PROVIDER_SETTING_NAME = 'Web_AddressSearchProvider'
const REGION_FIELDS = ['postcode', 'place', 'municipality', 'province']

const { sameValue } = addressSearchUtils

/**
 * Editor that searches an address register and fills all location fields of the project at once,
 * including fields that are not active in the template. It has no value of its own.
 */
export default {
  name: 'addressSearchEditor',
  props: ['field', 'functions', 'options'],

  data() {
    return {
      id: this._uid,
      valid: true,
      title: '',
      query: '',
      suggestions: [],
      activeIndex: 0,
      searching: false,
      searchError: false,
      resolving: false,
      requestCounter: 0,
      providerId: DEFAULT_PROVIDER_ID,
      providerChosen: false,
      preview: null,
      lastApply: null,
      // values filled in by this search since the project was last saved: { key: value }
      unsavedValues: {}
    }
  },

  computed: {
    workingObject() {
      return this.$store.state.workingObject || {}
    },
    marks() {
      return this.$store.state.addressSearchMarks
    },
    providerOptions() {
      const ids = [DEFAULT_PROVIDER_ID, ...Object.keys(PROVIDERS).filter((id) => id !== DEFAULT_PROVIDER_ID)]
      return ids.map((id) => ({ id, label: this.$t('addressSearch.providers.' + id) }))
    },
    showSuggestions() {
      return this.query.trim().length >= MIN_QUERY_LENGTH
    },
    conflictCount() {
      return this.preview ? this.preview.rows.filter((row) => row.previous).length : 0
    },
    applyCount() {
      return this.preview ? this.preview.rows.filter((row) => row.selected && row.value.trim() !== '').length : 0
    },
    canUndo() {
      if (!this.lastApply) return false
      // Once a filled field has been changed by hand, undo would silently revert that change too.
      return Object.keys(this.lastApply.appliedValues).every((key) => sameValue(this.workingObject[key], this.lastApply.appliedValues[key]))
    }
  },

  watch: {
    '$store.state.workingObjectChanged'(changed) {
      if (!changed) this.unsavedValues = {}
    }
  },

  created() {
    this.searchDebounced = _.debounce(this.search, 250)
    this.loadSavedProvider()
  },

  mounted() {
    this.title = this.field.title
  },

  methods: {
    async loadSavedProvider() {
      try {
        const response = await this.$store.dispatch('getSettingByName', { settingName: PROVIDER_SETTING_NAME })
        const saved = response && response.data && response.data.SettingValue
        // The user may already have picked a source while the setting was loading.
        if (PROVIDERS[saved] && !this.providerChosen) this.providerId = saved
      } catch (error) {
        console.error(`Unable to load ${PROVIDER_SETTING_NAME}, starting with ${DEFAULT_PROVIDER_ID}`, error)
      }
    },

    onProviderChange() {
      this.providerChosen = true
      this.$store.dispatch('updateSetting', { settingName: PROVIDER_SETTING_NAME, settings: this.providerId })
        .catch((error) => console.error(`Unable to save ${PROVIDER_SETTING_NAME}`, error))
      this.search()
    },

    focus() {
      setTimeout(() => {
        $('#address-search-' + this.id).focus()
      }, 200)
    },

    async search() {
      this.searchDebounced.cancel()
      const query = this.query.trim()
      if (query.length < MIN_QUERY_LENGTH) {
        this.suggestions = []
        return
      }
      const request = ++this.requestCounter
      this.searching = true
      this.searchError = false
      try {
        const suggestions = await suggestAddresses(this.providerId, query)
        if (request !== this.requestCounter) return
        this.suggestions = suggestions
        this.activeIndex = 0
      } catch (error) {
        if (request !== this.requestCounter) return
        console.error(error)
        this.searchError = true
      } finally {
        if (request === this.requestCounter) this.searching = false
      }
    },

    onSearchKeydown(event) {
      if (!this.showSuggestions || !this.suggestions.length) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.activeIndex = (this.activeIndex + 1) % this.suggestions.length
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.choose(this.suggestions[this.activeIndex])
      }
    },

    suggestionLabel(suggestion) {
      if (!suggestion.missingHouseNumber) return suggestion.label
      const [street, ...rest] = suggestion.label.split(',')
      return [`${street} ${suggestion.missingHouseNumber}`, ...rest].join(',')
    },

    async choose(suggestion) {
      this.resolving = true
      this.searchError = false
      try {
        const address = await resolveAddress(this.providerId, suggestion)
        this.preview = this.buildPreview(address, suggestion)
      } catch (error) {
        console.error(error)
        this.searchError = true
      } finally {
        this.resolving = false
      }
    },

    backToSearch() {
      this.preview = null
      this.focus()
    },

    isInactive(key) {
      return typeof this.field.isFieldActive === 'function' && !this.field.isFieldActive(key)
    },

    /**
     * One row per field: the value to fill in ('' empties the field), which the user can change
     * before applying, and the value it replaces. A value this search filled in is not shown as
     * previous value until it has been saved. Every row can be unticked to leave its field alone.
     */
    buildPreview(address, suggestion) {
      const wo = this.workingObject
      const rows = []
      const addRow = (id, value, notInRegister = false) => {
        rows.push({ id, key: PROJECT_ADDRESS_FIELDS[id], value, suggested: value, notInRegister })
      }

      addRow('name', address.precision === SUGGESTION_TYPES.PLACE
        ? address.place
        : `${address.street}${address.houseNumber ? ' ' + address.houseNumber : ''}, ${address.place}`)
      if (address.precision !== SUGGESTION_TYPES.PLACE) {
        addRow('street', address.street)
        addRow('houseNumber', address.houseNumber, !!address.houseNumberNotInRegister)
      }
      REGION_FIELDS.forEach((id) => {
        if (address[id]) addRow(id, address[id])
      })

      rows.forEach((row) => {
        const current = wo[row.key]
        row.previous = current && !sameValue(current, row.value) && !sameValue(current, this.unsavedValues[row.key]) ? current : ''
        // A value the user entered is not emptied unless asked for, e.g. a typed house number when
        // only a street is chosen.
        row.selected = !(row.previous && row.value === '')
        row.label = this.$t('addressSearch.fields.' + row.id)
        row.notes = []
        if (row.notInRegister) row.notes.push({ kind: 'warning', text: this.$t('addressSearch.notes.notInRegister') })
        if (this.isInactive(row.key)) row.notes.push({ kind: 'inactive', text: this.$t('addressSearch.notes.inactive') })
      })

      return {
        rows,
        title: this.suggestionLabel(suggestion),
        source: this.$t('addressSearch.providers.' + address.providerId),
        callout: this.getCallout(address)
      }
    },

    getCallout(address) {
      if (address.houseNumberNotInRegister) return this.$t('addressSearch.callouts.notInRegister', { houseNumber: address.houseNumber })
      if (address.precision === SUGGESTION_TYPES.STREET) return this.$t('addressSearch.callouts.street')
      if (address.precision === SUGGESTION_TYPES.PLACE) return this.$t('addressSearch.callouts.place')
      return ''
    },

    apply() {
      const values = {}
      const previousValues = {}
      const previousMarks = this.marks
      const marks = { ...this.marks }
      this.preview.rows.filter((row) => row.selected).forEach((row) => {
        const current = this.workingObject[row.key]
        const value = row.value.trim()
        if (!sameValue(current, value)) {
          values[row.key] = value
          previousValues[row.key] = current === undefined ? '' : current
        }
        if (value === '') {
          delete marks[row.key]
          return
        }
        // The mark holds the suggested value, so a value changed in the preview shows as edited.
        const kind = row.notInRegister ? ADDRESS_SEARCH_MARK_KINDS.NOT_IN_REGISTER : ADDRESS_SEARCH_MARK_KINDS.AUTO
        marks[row.key] = { kind, source: this.preview.source, value: row.suggested }
      })

      // updateWorkingObject adds its own fields to the payload, so it gets a copy.
      if (Object.keys(values).length) this.$store.commit('updateWorkingObject', { ...values })
      this.$store.commit('setAddressSearchMarks', marks)
      this.lastApply = { previousValues, previousMarks, appliedValues: values }
      this.unsavedValues = { ...this.unsavedValues, ...values }

      this.preview = null
      this.query = ''
      this.suggestions = []
      this.$parent.hidePanel(true)
    },

    undo() {
      const { previousValues, previousMarks } = this.lastApply
      if (Object.keys(previousValues).length) this.$store.commit('updateWorkingObject', { ...previousValues })
      this.$store.commit('setAddressSearchMarks', previousMarks)
      this.lastApply = null
    }
  }
}
</script>

<style lang="less" scoped>
.address-search {
  // The panel style keeps sub names on one line; this one is a sentence.
  .field-sub-name {
    white-space: normal;
  }

  // The icon sits inside the field, so the field keeps the size and corners of the other fields.
  .address-search-trigger {
    position: relative;

    .form-control {
      padding-left: 32px;
    }

    .glyphicon {
      position: absolute;
      top: 50%;
      left: 12px;
      padding: 0;
      transform: translateY(-50%);
      z-index: 3;
      color: @gray500;
      pointer-events: none;
    }
  }

  .address-search-undo {
    margin: 4px 0 0;
    font-size: 12px;

    a {
      color: @greenTerra800;
      text-decoration: underline;
    }
  }

  .address-search-source {
    margin-top: 6px;

    select {
      background: transparent;
      border: 0;
      border-bottom: 1px dashed @gray300;
      color: @white;

      option {
        color: @gray900;
      }
    }
  }

  .address-search-suggestions {
    margin-top: 10px;
    max-height: calc(100vh - 360px);
    overflow-y: auto;
  }

  .address-search-suggestion {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 10px;
    cursor: pointer;
    border-radius: 0;

    &.active,
    &:hover {
      background-color: @greenTerra100;
      border-color: @gray200;
      color: @gray900;
    }
  }

  .address-search-suggestion-label {
    flex: 1 1 auto;
    font-weight: 600;
  }

  .address-search-suggestion-type {
    font-size: 11px;
    text-transform: uppercase;
    color: @gray500;
  }

  .address-search-suggestion-warning {
    flex-basis: 100%;
    font-size: 12px;
    color: @warning700;
  }

  .address-search-state {
    color: @gray500;
  }

  .address-search-preview-title {
    color: @white;
    margin: 6px 0 8px;

    strong {
      font-size: 15px;
      margin-right: 10px;
    }

    span {
      font-size: 12px;
      color: @gray300;
    }
  }

  .address-search-callout {
    color: @white;
    border-left: 3px solid @warning300;
    background-color: rgba(254, 200, 75, 0.12);
    padding: 8px 12px;
  }

  .address-search-preview {
    width: 100%;
    color: @white;
    margin-bottom: 14px;

    td {
      padding: 7px 8px 7px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.16);
      vertical-align: top;
    }

    .address-search-preview-check {
      width: 26px;
    }

    .address-search-preview-label {
      width: 140px;
      color: @gray300;
    }

    .address-search-preview-value {
      font-weight: 600;

      .previous {
        display: block;
        font-weight: 400;
        color: @gray400;
        text-decoration: line-through;
      }

      .form-control {
        font-weight: 600;
      }

      .note {
        display: block;
        font-weight: 400;
        font-size: 12px;
        color: @gray300;

        &.warning {
          color: @warning300;
        }

        &.inactive {
          color: #a9d8ff;
        }
      }
    }

    tr.skipped .address-search-preview-value {
      opacity: 0.45;
    }
  }

  .address-search-actions .btn + .btn {
    margin-left: 8px;
  }

  .address-search-apply {
    background-color: @primaryGreen;
    border-color: @greenTerra700;
    color: @white;
    font-weight: 600;

    &:hover,
    &:focus {
      background-color: @greenTerra700;
      color: @white;
    }
  }
}
</style>
